"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { trackInteraction } from "@/utils/trackInteraction";
import { getCookieValue } from "@/utils/getCookieValue";
import { getMetaData } from "@/utils/getMetaData";

export default function useTimeOnPageTracker() {
  const pathname = usePathname();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const secondsElapsedRef = useRef<number>(0);
  const isActiveRef = useRef<boolean>(true);

  useEffect(() => {
    secondsElapsedRef.current = 0;
    isActiveRef.current = true;

    const tick = () => {
      if (isActiveRef.current) {
        secondsElapsedRef.current += 10;

        trackInteraction(
          {
            event_label: "user_engagement",
            time_on_page_seconds: secondsElapsedRef.current,
            page_path: window.location.pathname,
            page_url: window.location.href,
            referrer: sessionStorage.getItem("isov_referrer") ?? "direct",
            timestamp: new Date().toISOString(),
            identity: getCookieValue("hubspotutk"),
            content_tags: getMetaData("content-tag"),
          },
          "user_engagement",
        );
      }
    };

    intervalRef.current = setInterval(tick, 10000);

    const handleVisibilityChange = () => {
      isActiveRef.current = document.visibilityState === "visible";
    };

    const handleUnloadOrHide = () => {
      if (secondsElapsedRef.current > 0) {
        trackInteraction(
          {
            event_label: "user_engagement_total",
            time_on_page_total_seconds: secondsElapsedRef.current,
            page_path: window.location.pathname,
            page_url: window.location.href,
            referrer: sessionStorage.getItem("isov_referrer") ?? "direct",
            timestamp: new Date().toISOString(),
            content_tags: getMetaData("content-tag"),
          },
          "user_engagement_total",
        );
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handleUnloadOrHide);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handleUnloadOrHide);
    };
  }, [pathname]);
}
